import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    MenuItem,
    TextField,
    Typography,
    IconButton,
    Collapse,
    CircularProgress,
    Button,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp, Save, Receipt } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import consolidationService from "../../services/consolidationService";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import StatusChip from "../../components/common/StatusChip";
import ConsolidationDetailDialog from "../../components/common/ConsolidationDetailDialog";
import { useSelector } from "react-redux";
import { selectStatusOptions, selectKeyForRole, selectStatusOrder } from "../../redux/slices/statusDefinitionsSlice";

const AdminConsolidations = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const statusOptions = useSelector(selectStatusOptions("consolidation"));
    const itemReceivedKey = useSelector(selectKeyForRole("item", "received"));
    const itemStatusOrder = useSelector(selectStatusOrder("item"));
    const receivedIdx = itemStatusOrder.indexOf(itemReceivedKey);
    // How many of this consolidation's items have reached (or passed)
    // "received" — itemStatus is now resolved live from the shipment on
    // every fetch (see consolidationController.resolveLiveConsolidationItem),
    // so this always reflects the item's real current stage, not a frozen
    // snapshot from whenever it joined the consolidation.
    const receivedCount = (items) =>
        items.filter((it) => {
            const idx = it.itemStatus ? itemStatusOrder.indexOf(it.itemStatus) : -1;
            return receivedIdx !== -1 && idx !== -1 && idx >= receivedIdx;
        }).length;
    const [consolidations, setConsolidations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [itemsById, setItemsById] = useState({}); // consolidationId -> items[] (with itemStatus), lazily fetched
    const [loadingItemsId, setLoadingItemsId] = useState(null);

    // Admin-configurable window (days from creation) during which more
    // items/shipments can still be added to a consolidation — see
    // consolidationController.addItemToConsolidation, which enforces it.
    const [addWindowDaysInput, setAddWindowDaysInput] = useState("14");
    const [savedAddWindowDays, setSavedAddWindowDays] = useState(14);
    const [savingWindow, setSavingWindow] = useState(false);

    useEffect(() => {
        consolidationService
            .getSettings()
            .then(({ data }) => {
                const days = data.data?.consolidationSettings?.addWindowDays ?? 14;
                setAddWindowDaysInput(String(days));
                setSavedAddWindowDays(days);
            })
            .catch(() => enqueueSnackbar("Failed to load consolidation settings", { variant: "error" }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveAddWindow = async () => {
        const days = parseInt(addWindowDaysInput, 10);
        if (!Number.isFinite(days) || days < 1) {
            enqueueSnackbar("Enter a whole number of at least 1", { variant: "error" });
            return;
        }
        setSavingWindow(true);
        try {
            await consolidationService.updateSettings({ addWindowDays: days });
            setSavedAddWindowDays(days);
            enqueueSnackbar("Consolidation window updated", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to update", { variant: "error" });
        } finally {
            setSavingWindow(false);
        }
    };

    const toggleExpand = async (e, id) => {
        e.stopPropagation();
        if (expandedId === id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(id);
        if (itemsById[id]) return;
        setLoadingItemsId(id);
        try {
            const { data } = await consolidationService.getOne(id);
            setItemsById((prev) => ({ ...prev, [id]: data.data.consolidation.items || [] }));
        } catch {
            enqueueSnackbar("Failed to load consolidation items", { variant: "error" });
        } finally {
            setLoadingItemsId(null);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await consolidationService.getAll({ status: statusFilter || undefined });
            setConsolidations(data.data.consolidations || []);
        } catch {
            enqueueSnackbar("Failed to load consolidations", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Consolidations"
                subtitle="Customers' requests to have a shipment's items sailed and delivered together"
            />

            <Card>
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Add Window
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
                        Days after a consolidation is created during which more items/shipments can still be added to
                        it. Currently <strong>{savedAddWindowDays}</strong> day(s).
                    </Typography>
                    <TextField
                        size="small"
                        type="number"
                        label="Days"
                        value={addWindowDaysInput}
                        onChange={(e) => setAddWindowDaysInput(e.target.value)}
                        sx={{ width: 100 }}
                        slotProps={{ htmlInput: { min: 1 } }}
                    />
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Save />}
                        onClick={handleSaveAddWindow}
                        disabled={savingWindow || parseInt(addWindowDaysInput, 10) === savedAddWindowDays}
                    >
                        {savingWindow ? <CircularProgress size={16} /> : "Save"}
                    </Button>
                </CardContent>
            </Card>

            <Card>
                <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                    <TextField
                        select
                        size="small"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ width: 200 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {statusOptions.map((s) => (
                            <MenuItem key={s.value} value={s.value}>
                                {s.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>

                {loading ? (
                    <SkeletonLoader rows={5} />
                ) : consolidations.length === 0 ? (
                    <EmptyState
                        icon="📦"
                        title="No consolidations"
                        description="Consolidations are started by a customer or admin from a shipment's items."
                    />
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 700 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 40 }} />
                                    <TableCell>Consolidation #</TableCell>
                                    <TableCell>Customer</TableCell>
                                    <TableCell>Received / Total Items</TableCell>
                                    <TableCell>Shipments</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Requested</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {consolidations.map((c) => {
                                    const shipmentCount = new Set(
                                        c.items.map((it) => it.shipment?._id || it.shipment),
                                    ).size;
                                    const isExpanded = expandedId === c._id;
                                    const items = itemsById[c._id];
                                    return (
                                        <React.Fragment key={c._id}>
                                            <TableRow hover sx={{ cursor: "pointer" }} onClick={() => setSelectedId(c._id)}>
                                                <TableCell>
                                                    <IconButton size="small" onClick={(e) => toggleExpand(e, c._id)}>
                                                        {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{c.consolidationNumber}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {c.customer?.firstName} {c.customer?.lastName}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{receivedCount(c.items)}/{c.items.length}</TableCell>
                                                <TableCell>{shipmentCount}</TableCell>
                                                <TableCell>
                                                    <StatusChip status={c.status} domain="consolidation" />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                                    {dayjs(c.createdAt).format("MMM D, YYYY")}
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<Receipt sx={{ fontSize: 14 }} />}
                                                        onClick={() => navigate(`/admin/generate-invoice?consolidationId=${c._id}`)}
                                                    >
                                                        Invoice Consolidation
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={8}
                                                    sx={{
                                                        // The global theme's `.MuiTableCell-body` override
                                                        // (theme.js MuiTableBody styleOverrides) sets
                                                        // padding+borderBottom via a 2-class selector, which
                                                        // beats a plain sx prop on specificity — without
                                                        // !important that padding/border shows through even
                                                        // fully collapsed, as a permanent gap between rows.
                                                        padding: "0px !important",
                                                        borderBottom: isExpanded ? undefined : "0 !important",
                                                    }}
                                                >
                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                        <Box sx={{ p: 2, bgcolor: "action.hover" }}>
                                                            {loadingItemsId === c._id ? (
                                                                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                                                    <CircularProgress size={20} />
                                                                </Box>
                                                            ) : items && items.length > 0 ? (
                                                                <Table size="small">
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell>Item</TableCell>
                                                                            <TableCell>Tracking No.</TableCell>
                                                                            <TableCell>Shipment</TableCell>
                                                                            <TableCell>Order #</TableCell>
                                                                            <TableCell>D/R #</TableCell>
                                                                            <TableCell>MBL #</TableCell>
                                                                            <TableCell>Item Status</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {items.map((it) => (
                                                                            <TableRow key={it.itemId || it._id}>
                                                                                <TableCell>{it.description || "—"}</TableCell>
                                                                                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                                                    {it.trackingNumber || "—"}
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                                                    {it.shipment?.trackingNumber || "—"}
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                                                    {it.shipment?.orderNumber || "—"}
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                                                    {it.dockReceiptNumber || "—"}
                                                                                </TableCell>
                                                                                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                                                    {it.masterBillNumber || "—"}
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                    {it.itemStatus ? (
                                                                                        <StatusChip status={it.itemStatus} domain="item" />
                                                                                    ) : (
                                                                                        "—"
                                                                                    )}
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No items on this consolidation.
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Card>

            <ConsolidationDetailDialog
                open={!!selectedId}
                onClose={() => setSelectedId(null)}
                consolidationId={selectedId}
                isAdmin
                onChanged={load}
            />
        </Box>
    );
};

export default AdminConsolidations;
